import { useQuery } from "@tanstack/react-query";
import Balance from "./components/Balance";
import CourseTable from "./components/CourseTable";
import Invoice from "./components/Invoice";
import PaymentMethod from "./components/PaymentMethod";
import YourCard from "./components/YourCard";
import YourTransaction from "./components/YourTransaction";
import YourTransfers from "./components/YourTransfer";

import { fetchReferralCodesByUserId, fetchMyBillings, fetchInvoicesByUserId, getUserProfile, fetchCoursesByUserId, fetchRemainingSubscriptionTime } from "api";
import Loader from "components/loaders/Loader";

const Billing = () => {
  // Load user profile first (essential)
  const {
    data: userProfile,
    isLoading: isUserProfileLoading,
    error: userProfileError,
  } = useQuery({
    queryKey: ["userProfile"],
    queryFn: getUserProfile,
  });

  const userId = userProfile?.user.id;

  // Load all other data in parallel (non-blocking)
  const {
    data: billings = [],
    isLoading: isBillingsLoading,
    error: billingsError,
  } = useQuery({
    queryKey: ["myBillings"],
    queryFn: fetchMyBillings,
    enabled: !!userProfile,
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
  });

  const { 
    data: invoices = [], 
    isLoading: isInvoicesLoading, 
    error: invoicesError 
  } = useQuery({
    queryKey: ["invoices", userId],
    queryFn: () => fetchInvoicesByUserId(userId),
    enabled: !!userId,
  });

  const {
    data: courses = [],
    isLoading: isCoursesLoading,
    error: coursesError,
  } = useQuery({
    queryKey: ["courses", userId],
    queryFn: () => fetchCoursesByUserId(userId),
    enabled: !!userId,
  });

  const {
    data: couponsData = [],
    isLoading: isCouponsLoading,
    error: couponsError,
  } = useQuery({
    queryKey: ["coupons", userId],
    queryFn: () => fetchReferralCodesByUserId(userId),
    enabled: !!userId,
  });

  const {
    data: remainingSubscriptions = [],
    isLoading: isRemainingLoading,
    error: remainingError,
  } = useQuery({
    queryKey: ["remainingSubscriptions", userId],
    queryFn: () => fetchRemainingSubscriptionTime(userId),
    enabled: !!userId,
  });

  // Only show global loader for user profile (essential data)
  if (isUserProfileLoading) {
    return <Loader />;
  }

  // Log errors but don't block rendering
  if (userProfileError) console.error("Error loading user profile:", userProfileError);
  if (billingsError) console.error("Error loading billings:", billingsError);
  if (invoicesError) console.error("Error loading invoices:", invoicesError);
  if (coursesError) console.error("Error loading courses:", coursesError);
  if (couponsError) console.error("Error loading coupons:", couponsError);
  if (remainingError) console.error("Error loading remaining subscriptions:", remainingError);

  // Transform data
  const coupons = Array.isArray(couponsData) ? couponsData.map((coupon) => ({
    title: `${coupon.discount_percentage}% Off`,
    expires: coupon.updatedAt,
    isActive: coupon.max_usage > coupon.times_used,
  })) : [];

  const columnsData = [
    { Header: "COURSE NAME", accessor: "name" },
    { Header: "STATUS", accessor: "status" },
    { Header: "SUBSCRIPTION TYPE", accessor: "subscriptionType" },
  ];

  // Use remainingSubscriptions data instead of courses for proper expiration tracking
  const tableData = remainingSubscriptions.map((subscription) => {
    const isExpired = new Date(subscription.expirationDate) <= new Date();
    return {
      name: subscription.courseTitle,
      status: isExpired ? 'expired' : 'active',
      subscriptionType: subscription.subscriptionType || "Standard",
      courseId: subscription.courseId || subscription.id,
      enrolledDate: subscription.subscriptionDate || subscription.createdAt,
      expirationDate: subscription.expirationDate,
    };
  });

  return (
    <div className="mt-3 w-full">
      <div className="mb-5 grid grid-cols-1 gap-5 xl:grid-cols-3">
        <YourCard />
        <div className="h-full w-full rounded-[20px]">
          {isRemainingLoading ? (
            <div className="min-h-[200px] flex items-center justify-center">
              <Loader size="sm" />
            </div>
          ) : (
            <Balance transactions={billings} subscriptions={remainingSubscriptions}/>
          )}
          <PaymentMethod userId={userProfile?.user.id} initialMpesaNumber={userProfile?.user?.phoneNumber}/>
        </div>
        {isInvoicesLoading ? (
          <div className="min-h-[200px] flex items-center justify-center">
            <Loader size="sm" />
          </div>
        ) : (
          <Invoice invoices={invoices.map((invoice) => ({ ...invoice, orderId: invoice.orderId }))} />
        )}
      </div>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-3">
        <div>
          {isBillingsLoading ? (
            <div className="min-h-[200px] flex items-center justify-center">
              <Loader size="sm" />
            </div>
          ) : (
            <YourTransaction transactions={billings}/>
          )}
        </div>
        <div>  
          {isCouponsLoading ? (
            <div className="min-h-[200px] flex items-center justify-center">
              <Loader size="sm" />
            </div>
          ) : (
            <YourTransfers appliedCoupons={coupons} />
          )}
        </div>
        <div>         
          {isRemainingLoading ? (
            <div className="min-h-[200px] flex items-center justify-center">
              <Loader size="sm" />
            </div>
          ) : (
            <CourseTable columnsData={columnsData} tableData={tableData} />
          )}
        </div>
      </div>
    </div>
  );
};

export default Billing;
